// =============================================================================
// Customer domain helpers
// =============================================================================

export type CustomerStatus = "regular" | "vip" | "blocked";
export type CustomerSource = "whatsapp" | "storefront" | "walkin" | "manual";
export type CustomerFilter = "all" | "new" | "vip" | "regular" | "blocked";
export type CustomerAddressSource = "ai_agent" | "staff" | "customer_self";

export interface CustomerRecord {
  id: string;
  tenant_id: string;
  name: string;
  phone: string;
  email: string | null;
  /** @deprecated Prefer address_line — kept for legacy rows and CSV export */
  address: string | null;
  address_line: string | null;
  address_lat: number | null;
  address_lng: number | null;
  address_place_id: string | null;
  address_source: CustomerAddressSource | null;
  address_updated_at: string | null;
  notes: string | null;
  status: CustomerStatus;
  source: CustomerSource | null;
  total_orders: number;
  total_spent: number;
  points_balance: number;
  referred_by_affiliate_id: string | null;
  last_order_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerOrderSummary {
  id: string;
  displayNumber: string;
  itemCount: number;
  status: string;
  total: number;
}

export type CustomerOrderDisplayStatus = "preparing" | "completed" | "cancelled" | "pending" | "other";

export interface CustomerStats {
  new_this_month: number;
  repeat_customers: number;
  vip_count: number;
}

export interface CustomerListCursor {
  lastOrderAt: string | null;
  id: string;
}

export const CUSTOMERS_PAGE_SIZE = 25;

const NEW_CUSTOMER_DAYS = 7;

export function normalizePhone(phone: string): string {
  return phone.replace(/\s+/g, " ").trim();
}

export function getCustomerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]!;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1]!;
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
}

export type CustomerDisplayStatus = "blocked" | "vip" | "new" | "regular";

export function isNewCustomer(customer: Pick<CustomerRecord, "created_at">, now = new Date()): boolean {
  const created = new Date(customer.created_at);
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - NEW_CUSTOMER_DAYS);
  return created >= cutoff;
}

export function getCustomerDisplayStatus(customer: CustomerRecord): CustomerDisplayStatus {
  if (customer.status === "blocked") return "blocked";
  if (customer.status === "vip") return "vip";
  if (isNewCustomer(customer)) return "new";
  return "regular";
}

export function matchesCustomerFilter(
  customer: CustomerRecord,
  filter: CustomerFilter,
  now = new Date()
): boolean {
  if (filter === "all") return true;
  if (filter === "new") return isNewCustomer(customer, now);
  if (filter === "vip") return customer.status === "vip";
  if (filter === "regular") return customer.status === "regular";
  if (filter === "blocked") return customer.status === "blocked";
  return true;
}

export function formatCustomerLastOrder(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return "—";
  }
}

export function getCustomerAddressLine(
  customer: Pick<CustomerRecord, "address_line" | "address">
): string | null {
  const line = customer.address_line?.trim() || customer.address?.trim();
  return line || null;
}

export function hasCustomerAddress(
  customer: Pick<CustomerRecord, "address_line" | "address" | "address_lat" | "address_lng">
): boolean {
  return Boolean(getCustomerAddressLine(customer));
}

export function hasCustomerCoordinates(
  customer: Pick<CustomerRecord, "address_lat" | "address_lng">
): boolean {
  return customer.address_lat != null && customer.address_lng != null;
}

export function getCustomerMapsUrl(
  customer: Pick<CustomerRecord, "address_lat" | "address_lng" | "address_line" | "address">
): string | null {
  if (hasCustomerCoordinates(customer)) {
    return `https://maps.google.com/?q=${customer.address_lat},${customer.address_lng}`;
  }
  const line = getCustomerAddressLine(customer);
  if (line) return `https://maps.google.com/?q=${encodeURIComponent(line)}`;
  return null;
}

export function canNavigateToCustomer(
  customer: Pick<CustomerRecord, "address_lat" | "address_lng" | "address_line" | "address">
): boolean {
  return getCustomerMapsUrl(customer) != null;
}

export function formatCustomerLastOrderRelative(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (diffDays === 0) {
      const diffHours = Math.floor((now - then) / (1000 * 60 * 60));
      if (diffHours === 0) return rtf.format(0, "day");
      return rtf.format(-diffHours, "hour");
    }
    return rtf.format(-diffDays, "day");
  } catch {
    return "—";
  }
}

export function formatOrderDisplayNumber(id: string): string {
  const compact = id.replace(/-/g, "");
  const num = parseInt(compact.slice(-4), 16) % 10000;
  return `#${num || compact.slice(0, 4)}`;
}

const PREPARING_STATUSES = new Set([
  "in_kitchen",
  "approved",
  "pending_cashier_approval",
  "ready_for_dispatch",
]);
const COMPLETED_STATUSES = new Set(["completed", "delivered", "picked_up", "out_for_delivery"]);

export function mapOrderDisplayStatus(status: string): CustomerOrderDisplayStatus {
  if (status === "cancelled") return "cancelled";
  if (PREPARING_STATUSES.has(status)) return "preparing";
  if (COMPLETED_STATUSES.has(status)) return "completed";
  if (status === "draft") return "pending";
  return "other";
}

export function buildCustomersCsv(
  customers: CustomerRecord[],
  labels: {
    name: string;
    phone: string;
    email: string;
    address: string;
    status: string;
    totalOrders: string;
    totalSpent: string;
    lastOrder: string;
  },
  formatSpent: (amount: number) => string
): string {
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const header = [
    labels.name,
    labels.phone,
    labels.email,
    labels.address,
    labels.status,
    labels.totalOrders,
    labels.totalSpent,
    labels.lastOrder,
  ].join(",");

  const rows = customers.map((c) =>
    [
      escape(c.name),
      escape(c.phone),
      escape(c.email ?? ""),
      escape(getCustomerAddressLine(c) ?? ""),
      escape(c.status),
      String(c.total_orders),
      escape(formatSpent(Number(c.total_spent))),
      escape(c.last_order_at ?? ""),
    ].join(",")
  );

  return [header, ...rows].join("\n");
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function shouldShowBlockConfirm(): boolean {
  const key = `oelox-customers-block-confirm-${new Date().toISOString().slice(0, 10)}`;
  if (sessionStorage.getItem(key)) return false;
  sessionStorage.setItem(key, "1");
  return true;
}
